import { type ReactElement, type ReactNode } from 'react';
import { getContextMessage } from '@/shared/copy/context';
import { getErrorCopy } from '@/shared/copy/errors';
import { isNotFoundError } from '@/shared/data/errors';
import { raisedPanelSx } from '@/shared/ui/panelShadowStyles';
import { layoutMeasures } from '@/theme/layoutMeasures';
import { iconSizes, s } from '@/theme/tokens';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { Box, CircularProgress, PageShell, Surface, Text } from '@/ui/primitives';
import { Button } from '@/ui/primitives/Button';
import { DataStateSkeleton } from '../DataStateSkeleton';
import { DataTableShell } from '../DataTableShell';
import { EmptyState } from '../EmptyState';
import { ErrorStatePanel } from '../ErrorStatePanel';
import { StatusBanner } from '../StatusBanner';
import type { ErrorInput } from '@/shared/data/errors';
import type { UiTone } from '@/theme/uiTokens';

type PageStateScaffoldProps = {
  session: object | null;
  sessionMessage: string;
  organisationId?: string | null;
  requireOrganisation?: boolean;
  organisationMessage?: string;
  projectId?: string | null;
  requireProject?: boolean;
  projectMessage?: string;
  requireAdmin?: boolean;
  isAdmin?: boolean;
  adminMessage?: string;
  adminFallback?: ReactNode;
  redirecting?: boolean;
  redirectMessage?: string;
  isLoading?: boolean;
  loadingMessage?: string;
  loadingFallback?: ReactNode;
  isError?: boolean;
  error?: ErrorInput;
  errorMessage?: string;
  empty?: boolean;
  emptyMessage?: string;
  emptyTone?: UiTone;
  variant?: 'table' | 'card';
  rows?: number;
  children: ReactNode;
};

export const PageStateScaffold = ({
  session,
  sessionMessage,
  organisationId = null,
  requireOrganisation = false,
  organisationMessage,
  projectId = null,
  requireProject = false,
  projectMessage,
  requireAdmin = false,
  isAdmin = false,
  adminMessage,
  adminFallback,
  redirecting = false,
  redirectMessage,
  isLoading = false,
  loadingMessage,
  loadingFallback,
  isError = false,
  error,
  errorMessage,
  empty = false,
  emptyMessage,
  variant = 'card',
  rows,
  children,
}: PageStateScaffoldProps): ReactElement => {
  const renderPanel = (title: string, body?: string): ReactElement => {
    if (variant === 'table') {
      return (
        <DataTableShell>
          <EmptyState title={title} body={body} surface={false} />
        </DataTableShell>
      );
    }

    return (
      <PageShell maxWidth="xs" paddingX="lg" paddingY="xl" paddingBottom="xl">
        <Stack gap="xl" sx={{ pt: s('5xl') }}>
          <Section>
            <Box sx={{ width: '100%' }}>
              <ErrorStatePanel title={title} body={body} sx={raisedPanelSx} />
            </Box>
          </Section>
        </Stack>
      </PageShell>
    );
  };

  const renderSkeleton = (): ReactElement => {
    if (variant === 'table') {
      return (
        <DataTableShell>
          <DataStateSkeleton variant="table" rows={rows} padding={null} />
        </DataTableShell>
      );
    }

    return (
      <Surface padding="md">
        <DataStateSkeleton variant="card" rows={rows} padding={null} />
      </Surface>
    );
  };

  const renderLoadingState = (message: string): ReactElement => (
    <Box
      sx={{
        minHeight: '45vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Stack gap="md" align="center">
        <Text role="body" color="textSecondary" align="center">
          {message}
        </Text>
        <CircularProgress size={iconSizes.lg} />
      </Stack>
    </Box>
  );

  const renderError = (message: string): ReactElement => {
    if (variant === 'table') {
      return (
        <DataTableShell>
          <StatusBanner tone="red" message={message} />
        </DataTableShell>
      );
    }

    return (
      <Box
        sx={{
          minHeight: layoutMeasures.viewport.contentMinHeight,
          display: 'grid',
          placeItems: 'center',
          width: '100%',
        }}
      >
        <PageShell maxWidth={false} paddingX="lg" paddingY="lg" sx={{ width: '100%' }}>
          <Surface padding="md" sx={{ width: 'fit-content', maxWidth: '100%', marginX: 'auto' }}>
            <Stack gap="md" align="center">
              <StatusBanner tone="red" message={message} />
              <Button
                variant="secondary"
                onClick={() => {
                  window.location.reload();
                }}
              >
                Reload
              </Button>
            </Stack>
          </Surface>
        </PageShell>
      </Box>
    );
  };

  if (!session) {
    return renderPanel(sessionMessage);
  }

  if (requireOrganisation && !organisationId) {
    return renderPanel(organisationMessage ?? getContextMessage('selection.workspace.continue'));
  }

  if (requireProject && !projectId) {
    return renderPanel(projectMessage ?? getContextMessage('selection.project.continue'));
  }

  if (isLoading) {
    if (loadingMessage) {
      return renderPanel(loadingMessage);
    }
    if (loadingFallback) {
      return <>{loadingFallback}</>;
    }
    return renderSkeleton();
  }

  if (isError) {
    const typedError = error as { message?: string } | null | undefined;
    const message = typedError?.message ?? errorMessage ?? getErrorCopy('banner.unexpectedError');
    if (isNotFoundError(error)) {
      return renderPanel(
        message,
        'This page may have been removed, or you may no longer have access to it.',
      );
    }
    return renderError(message);
  }

  if (requireAdmin && !isAdmin) {
    if (adminFallback) {
      return <>{adminFallback}</>;
    }
    return renderPanel(adminMessage ?? 'Admin access is required.');
  }

  if (redirecting) {
    return renderLoadingState(redirectMessage ?? 'Redirecting...');
  }

  if (empty) {
    return renderPanel(emptyMessage ?? 'Nothing here yet.');
  }

  return <>{children}</>;
};
